import { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, motion } from 'motion/react'
import clsx from 'clsx'
import { type CardIdQuery } from '../../utils/cardIdQuery'
import { store } from '../../stores/store'
import { useDecodedCardSharePayload } from '../../hooks/useDecodedCardSharePayload'
import { useParentsAppointmentActions } from '../../hooks/useParentsAppointmentActions'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import Button from '../ui/buttons/Button'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import AppointmentSelectionModal from './AppointmentSelectionModal'
import ParentsLandingLoadingScreen from './CardLandingLoadingScreen'
import TextHeadline from '../ui/texts/TextHeadline'


import { shareCardImageFromCanvas } from '../../utils/shareCardImage'
import IconDownload from '../ui/icons/IconDownload'
import TabControls from '../ui/controls/TabControls'
import { tabContainerVariants, tabButtonVariants } from '@/src/lib/motionVariants'
import ParentsInformationalOverlay from './ParentsInformationalOverlay'
import IconSkip from '../ui/icons/IconSkip'
import IconChevron from '../ui/icons/IconChevron'

type CardLandingScreenProps = {
    cardIdQuery: CardIdQuery
    className?: string
    onLearnMore?: () => void
    assetProgress?: number
    isLoadingError?: boolean
    onRetryLoading?: () => void
}

function ParentsBookingButton({ onPress }: { onPress: () => void }) {
    return (
        <div
            className={clsx(
                'flex justify-center bg-orange-stroke rounded-full w-full max-w-[min(100%,36rem)] h-auto min-h-8',
            )}
        >
            <motion.button
                type='button'
                initial={false}
                animate={{ y: -2.5 }}
                whileTap={{ y: 0 }}
                transition={{
                    y: {
                        type: 'spring',
                        stiffness: 520,
                        damping: 30,
                        mass: 0.7,
                    },
                }}
                className={clsx(
                    'relative flex justify-center items-center gap-2.5 bg-orange-gradient px-4 sm:px-6 py-2.5 border-2 border-orange-stroke rounded-full w-full min-h-8',
                )}
                onClick={onPress}
            >
                <div className='top-0.75 absolute bg-shine-gradient rounded-full w-[85%] h-2'></div>
                <span className='relative font-grandstander font-black text-[11px] text-white sm:text-[12px] text-center italic uppercase leading-snug tracking-[0.02em] [text-shadow:0_1px_0_#0A2897]'>
                    Termin vereinbaren
                    <br />
                    &amp; Sammelkarte abholen
                </span>
            </motion.button>
        </div>
    )
}

const CardLandingScreen = observer(function CardLandingScreen({
    cardIdQuery,
    className,
    onLearnMore,
    assetProgress = 0,
    isLoadingError = false,
    onRetryLoading,
}: CardLandingScreenProps) {
    const [showBookingCta, setShowBookingCta] = useState(false)
    const [downloadOk, setDownloadOk] = useState(false)
    const [barComplete, setBarComplete] = useState(false)
    const isStele = useSteleBreakpoint()

    /**
     * Decoded share payload from `?cID` (hearts, coins, battery/star/shield
     * power-ups, chameleon color + texture, recipient). Drives the
     * collectible-card visuals.
     */
    const cardSharePayload = useDecodedCardSharePayload(cardIdQuery)
    const isParentRecipient = cardSharePayload?.recipient === 'parent'
    const {
        mappingRows,
        showBranchPicker,
        setShowBranchPicker,
        openAppointment,
    } = useParentsAppointmentActions()

    useEffect(() => {
        if (!cardIdQuery.isPresent) {
            return
        }
        setShowBookingCta(false)
        setShowBranchPicker(false)
        setBarComplete(false)
        store.setParentsCardLandingActive(true)
        store.setParentsLandingShowUi(false)
        store.requestParentsLandingLevelComplete()
        return () => {
            store.setParentsCardLandingActive(false)
            store.setParentsLandingShowUi(false)
        }
    }, [cardIdQuery, setShowBranchPicker])

    const handleDownloadCardImage = useCallback(async () => {
        // The parent-landing card always renders the default (non-epic) variant
        // (its `topScoringPowerUp` is null because the shared payload only carries
        // counts, not point weights), so the visible card exports as `violet`.
        const ok = await shareCardImageFromCanvas({
            cardEpicEnabled: false,
            topScoringPowerUp: null,
        })
        if (!ok) {
            return
        }
        setDownloadOk(true)
        window.setTimeout(() => setDownloadOk(false), 2000)
    }, [])

    const handleExplainClick = useCallback(() => {
        setShowBookingCta(true)
    }, [])

    const handleOpenAppointmentLink = useCallback(() => {
        openAppointment(onLearnMore)
    }, [onLearnMore, openAppointment])

    const handleStartRun = useCallback(() => {
        const url = new URL(window.location.href)
        url.search = ''
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.hash}`)
        window.dispatchEvent(new PopStateEvent('popstate'))
        store.resetGame()
    }, [])

    const handleBarComplete = useCallback(() => {
        setBarComplete(true)
    }, [])

    const showLandingContent = store.parentsLandingShowUi && barComplete

    return (
        <>
            <AnimatePresence>
                {!barComplete ? (
                    <ParentsLandingLoadingScreen
                        key='parents-loading'
                        assetProgress={assetProgress}
                        loadingReady={store.parentsLandingShowUi}
                        isLoadingError={isLoadingError}
                        onRetryLoading={onRetryLoading ?? (() => { })}
                        onBarComplete={handleBarComplete}
                        isParentRecipient={isParentRecipient}
                    />
                ) : null}
            </AnimatePresence>
            {showLandingContent ? (
                <>
                    <AppointmentSelectionModal
                        open={showBranchPicker}
                        onClose={() => setShowBranchPicker(false)}
                        mappingRows={mappingRows}
                    />
                    <div
                        className={clsx(
                            'z-[85] absolute inset-0 flex px-4 py-2 pointer-events-none items-end justify-center',
                            className,
                        )}
                        // Decoded card-share stats
                        data-card-hearts={cardSharePayload?.remainingHearts}
                        data-card-coins={cardSharePayload?.coinsCollected}
                        data-card-battery={cardSharePayload?.batteryPowerUpsCollected}
                        data-card-star={cardSharePayload?.starPowerUpsCollected}
                        data-card-shield={cardSharePayload?.shieldPowerUpsCollected}
                        data-card-chameleon-color={cardSharePayload?.chameleonColor}
                        data-card-chameleon-texture={cardSharePayload?.chameleonTexture}
                    >
                        {/* {!isStele && (

                            <TabControls variants={tabContainerVariants} >
                                <motion.div variants={tabButtonVariants}>
                                    <ButtonIcon
                                        color='blue'
                                        variant='control'
                                        icon={<IconDownload className='w-full h-full' />}
                                        onClick={handleDownloadCardImage}
                                        aria-label='Karte zu meinen Bildern hinzufügen'
                                    />
                                </motion.div>
                            </TabControls>
                        )} */}

                        {isParentRecipient && !showBookingCta && (
                            <motion.div className='top-20 absolute flex flex-col justify-center items-center w-full'>
                                <TextHeadline colorPreset='red' fontSize={24} maxCharsPerLine={24}>Dein Kind hat's ins Ziel geschafft und Diese Karte freigespielt!</TextHeadline>
                            </motion.div>
                        )}

                        {showBookingCta && isParentRecipient ? (
                            <div className='flex justify-center pb-8 w-full pointer-events-none'>
                                <ParentsInformationalOverlay
                                    visible
                                    onAppointment={handleOpenAppointmentLink}
                                    onSaveCard={handleDownloadCardImage}
                                />
                            </div>
                        ) : showBookingCta ? (
                            <div className='flex justify-center pb-8 sm:pb-10 w-full max-w-[42rem] pointer-events-auto'>
                                <ParentsBookingButton onPress={handleOpenAppointmentLink} />
                            </div>
                        ) : (

                            isParentRecipient ? (
                                <>
                                    {/* <div className='flex flex-col items-center gap-2 md:gap-4 w-full'>
                                            <p className='text-white-stroke-even-darker font-nunito font-bold text-[16px] text-center leading-5.5 tracking-[-4%]'>Im Spiel hat es sich geschützt, vorgesorgt und jede Herausforderung bewältigt. Jetzt geht es um den Schutz im echten Leben.</p>
                                        </div> */}
                                    <div className='flex flex-col gap-1 md:gap-2 lg:gap-5 font-grandstander font-extrabold italic mb-6 items-center pointer-events-auto'>
                                        <Button color='blue' size='large' className='mx-6' onClick={handleExplainClick} icon={<IconChevron className='w-full h-full rotate-180' />}>
                                            Weiter
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <div
                                    className={clsx(
                                        'relative flex flex-col justify-center items-center bg-white-gradient drop-shadow-2-white-stroke border border-white-stroke w-full max-w-min max-h-min pointer-events-auto pattern-background',
                                        'px-4 py-6 rounded-3xl min-w-82 gap-4',
                                        'stele:px-16 stele:py-24 stele:rounded-[96px] min-stele:w-328 stele:gap-[64px]'
                                    )}
                                >
                                    <div className='flex flex-col items-center gap-2 md:gap-4 w-full'>
                                        <p className='text-white-stroke-even-darker font-nunito font-bold text-[16px] text-center leading-5.5 tracking-[-4%]'>Spiel deinen eigenen Lauf,  sammle Upgrades und erstelle deine eigene Karte.</p>
                                    </div>
                                    <div className='flex flex-col gap-1 md:gap-2 lg:gap-5 w-full font-grandstander font-extrabold italic'>
                                        <Button color='orange' size='large' onClick={handleStartRun}>
                                            Lauf Starten
                                        </Button>
                                    </div>
                                </div>
                            )

                        )}
                    </div>
                </>
            ) : null}
        </>
    )
})

export default CardLandingScreen


